<template>
  <div class="account-view">
    <!-- Success Modal -->
    <Modal
      v-model="showSuccessModal"
      :title="$t('account.upgrade.successTitle')"
      :dialog-class="'modal-dialog-centered'"
    >
      <div class="success-content">
        <div class="success-icon">
          <font-awesome-icon icon="circle-check" class="check-icon" />
        </div>
        <p>{{ $t('account.upgrade.successMessage') }}</p>
      </div>
      <template #footer>
        <div class="d-flex justify-content-center w-100">
          <button class="close-btn" @click="closeSuccessModal">
            {{ $t('account.upgrade.startLearning') }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Native Language Edit Modal -->
    <Modal
      v-model="isEditingNativeLanguage"
      :title="$t('account.userInfo.editNativeLanguageTitle')"
      :dialog-class="'modal-dialog-centered modal-dialog-native-lang'"
    >
      <p class="native-lang-modal-help">
        {{ $t('account.userInfo.editNativeLanguageHelp') }}
      </p>
      <LanguagePickerGrid
        :selected-code="editNativeLanguageCode"
        :disabled-base-codes="userInfo?.selectedLanguage ? [userInfo.selectedLanguage] : []"
        disabled-base-badge-key="languagePicker.activeLanguageBadge"
        :show-other-card="false"
        @pick="onPickNativeLanguage"
      />
      <div v-if="nativeLanguageConflict" class="conflict-warning">
        <font-awesome-icon icon="circle-exclamation" class="conflict-icon" />
        {{ $t('account.userInfo.nativeLanguageConflict') }}
      </div>
      <template #footer>
        <div class="native-lang-modal-actions">
          <button class="cancel-btn" @click="cancelEditNativeLanguage" :disabled="isSavingNativeLanguage">
            {{ $t('common.cancel') }}
          </button>
          <button
            class="confirm-btn"
            @click="saveNativeLanguage"
            :disabled="nativeLanguageConflict || isSavingNativeLanguage || !editNativeLanguage || !isNativeLanguageDirty"
          >
            <span v-if="!isSavingNativeLanguage">{{ $t('common.save') }}</span>
            <span v-else class="loading-text">
              <font-awesome-icon icon="spinner" spin />
              {{ $t('account.upgrade.processing') }}
            </span>
          </button>
        </div>
      </template>
    </Modal>

    <!-- Confirmation Modal -->
    <Modal
      v-model="showConfirmationModal"
      :title="$t('account.upgrade.confirmTitle')"
      :dialog-class="'modal-dialog-centered'"
    >
      <div class="plan-summary">
        <div class="plan-name">{{ confirmationDetails ? $t(confirmationDetails.nameKey) : '' }}</div>
        <div class="plan-price">
          ${{ confirmationDetails?.price }}<span class="period">{{ $t('account.upgrade.perMonth') }}</span>
        </div>
        <div v-if="selectedBillingCycle === 'yearly'" class="yearly-note">
          {{ $t('account.upgrade.billedAnnually', { amount: confirmationDetails?.yearlyTotal }) }}
        </div>
      </div>

      <div class="features-summary">
        <div class="feature-item">
          <font-awesome-icon icon="comments" class="feature-icon" />
          {{ $t('pricing.chatsPerMonth', { count: confirmationDetails?.chatsPerMonth }) }}
        </div>
        <div class="feature-item">
          <font-awesome-icon icon="graduation-cap" class="feature-icon" />
          {{ $t('pricing.lessonsPerMonth', { count: confirmationDetails?.lessonsPerMonth }) }}
        </div>
      </div>

      <div class="billing-note">
        <font-awesome-icon icon="info-circle" class="info-icon" />
        {{ $t('account.upgrade.billingNote', {
          amount: selectedBillingCycle === 'yearly' ? confirmationDetails?.yearlyTotal : confirmationDetails?.price,
          cycle: selectedBillingCycle
        }) }}
      </div>

      <template #footer>
        <div class="confirmation-actions">
          <button class="cancel-btn" @click="cancelUpgrade">{{ $t('common.cancel') }}</button>
          <button class="confirm-btn" :disabled="isProcessingUpgrade" @click="confirmUpgrade">
            <span v-if="!isProcessingUpgrade">{{ $t('account.upgrade.confirmButton') }}</span>
            <span v-else class="loading-text">
              <font-awesome-icon icon="spinner" spin />
              {{ $t('account.upgrade.processing') }}
            </span>
          </button>
        </div>
      </template>
    </Modal>

    <div class="container">
      <h1 class="page-title">{{ $t('account.pageTitle') }}</h1>

      <div class="account-sections">
        <!-- User Information Section -->
        <section class="account-section user-info-section">
          <h2 class="section-title">{{ $t('account.userInfo.title') }}</h2>
          <SkeletonLoader v-if="isLoading" type="profile" />
          <div v-else class="user-info-content">
            <div class="info-row">
              <div class="info-label">{{ $t('account.userInfo.name') }}</div>
              <div class="info-value">{{ userInfo?.name || $t('account.userInfo.notProvided') }}</div>
            </div>
            <div class="info-row">
              <div class="info-label">{{ $t('account.userInfo.nativeLanguage') }}</div>
              <div class="info-value">
                <span class="native-lang-display">
                  <img
                    v-if="currentNativeLanguageCode"
                    :src="flagsStore.getFlag(currentNativeLanguageCode)"
                    class="native-lang-flag"
                    alt=""
                  />
                  <span>{{ translateLanguageName(userInfo?.nativeLanguage) || $t('account.userInfo.notSet') }}</span>
                </span>
                <button class="edit-link" @click="startEditNativeLanguage">{{ $t('common.edit') }}</button>
                <!-- This control is the only way a signed-in user changes the UI
                     language, and nothing said so. Labelling, not a second
                     control: nativeLanguage stays the single source of truth. -->
                <span class="info-hint">{{ $t('account.userInfo.nativeLanguageHint') }}</span>
              </div>
            </div>
            <div class="info-row languages-list">
              <div class="info-label">{{ $t('account.userInfo.learningLanguage') }}</div>
              <div class="info-value">
                <div
                  v-for="code in heldLanguageCodes"
                  :key="code"
                  class="language-entry"
                >
                  <img :src="flagsStore.getFlag(code)" class="language-icon" alt="" />
                  <span>{{ languageName(code) }}</span>
                  <span v-if="code === userInfo?.selectedLanguage" class="badge active-badge">
                    {{ $t('languageSwitcher.activeLanguage') }}
                  </span>
                  <span v-if="languageMeta(code)" class="lang-meta">{{ languageMeta(code) }}</span>
                </div>
                <template v-if="heldLanguageCodes.length === 0">
                  {{ $t('account.userInfo.notSet') }}
                </template>
              </div>
            </div>
            <TutorVoicePicker
              v-if="userInfo?.selectedLanguage"
              :lang-code="userInfo.selectedLanguage"
            />
            <!-- Sits under the language list, for readers holding one language
                 and readers holding several alike. The override is per-language
                 and this edits the SELECTED language's, which is the same scope
                 (and the same limitation) TutorVoicePicker already ships with. -->
            <LanguageMixPicker
              v-if="userInfo?.selectedLanguage"
              :lang-code="userInfo.selectedLanguage"
            />
          </div>
        </section>

        <!-- Appearance Section -->
        <section class="account-section appearance-section">
          <h2 class="section-title">{{ $t('account.appearance.title') }}</h2>
          <div class="appearance-control" role="radiogroup" :aria-label="$t('account.appearance.title')">
            <button
              v-for="option in themeOptions"
              :key="option.value"
              type="button"
              class="appearance-option"
              :class="{ active: themeMode === option.value }"
              role="radio"
              :aria-checked="themeMode === option.value"
              @click="setThemeMode(option.value)"
            >
              <font-awesome-icon :icon="option.icon" />
              <span>{{ $t(option.labelKey) }}</span>
            </button>
          </div>
        </section>

        <!-- Notifications Section (native app only, feature-gated) -->
        <section
          v-if="featureFlags.mobileNotifications && isNativeApp"
          class="account-section notifications-section"
        >
          <h2 class="section-title">{{ $t('account.notifications.title') }}</h2>
          <p class="notifications-help">{{ $t('account.notifications.help') }}</p>
          <div
            v-for="category in notificationCategories"
            :key="category.id"
            class="notification-toggle-row"
          >
            <div class="toggle-text">
              <div class="toggle-label">{{ $t(category.labelKey) }}</div>
              <div class="toggle-description">{{ $t(category.descriptionKey) }}</div>
            </div>
            <button
              type="button"
              class="toggle-switch"
              :class="{ on: isCategoryEnabled(category) }"
              role="switch"
              :aria-checked="isCategoryEnabled(category)"
              :aria-label="$t(category.labelKey)"
              :disabled="isSavingNotificationPrefs"
              @click="toggleCategory(category)"
            >
              <span class="toggle-knob"></span>
            </button>
          </div>
          <div v-if="notificationPermissionDenied" class="conflict-warning">
            <font-awesome-icon icon="circle-exclamation" class="conflict-icon" />
            {{ $t('account.notifications.permissionDenied') }}
          </div>
        </section>

        <!-- Billing Section -->
        <section class="account-section billing-section">
          <h2 class="section-title">{{ $t('account.billing.title') }}</h2>
          <div class="billing-content">
            <div class="current-plan">
              <h3>{{ $t('account.billing.currentPlan') }}</h3>
              <!-- Gate on currentPlan itself, not isLoading: the two are
                   independent signals and can disagree (useLiveUserInfo clears
                   isLoading in its error callback while userInfo stays null). -->
              <div class="plan-details" v-if="currentPlan">
                <p class="plan-name">{{ $t(currentPlan.nameKey) }}</p>
                <div class="plan-features">
                  <div class="feature">
                    <span class="feature-label">
                      {{ isFreePlan ? $t('account.billing.chatsTotal') : $t('account.billing.chatsPerMonth') }}
                    </span>
                    <span class="feature-value">
                      {{ isFreePlan ? currentPlan.lifetimeChats : currentPlan.chatsPerMonth }}
                      <span v-if="isFreePlan" class="free-tier-badge">{{ $t('account.billing.freeTier') }}</span>
                    </span>
                  </div>
                  <div class="feature">
                    <span class="feature-label">
                      {{ isFreePlan ? $t('account.billing.lessonsTotal') : $t('account.billing.lessonsPerMonth') }}
                    </span>
                    <span class="feature-value">
                      {{ isFreePlan ? currentPlan.lifetimeLessons : currentPlan.lessonsPerMonth }}
                      <span v-if="isFreePlan" class="free-tier-badge">{{ $t('account.billing.freeTier') }}</span>
                    </span>
                  </div>
                  <!-- Remaining is free-tier only: UsageMeter already carries
                       the monthly remaining counts for paid plans. Hidden until
                       the one-shot lifetime read lands (or if it fails), so the
                       page never shows a wrong number. -->
                  <div v-if="isFreePlan && chatsRemaining !== null" class="feature">
                    <span class="feature-label">{{ $t('account.billing.chatsRemaining') }}</span>
                    <span class="feature-value u-tabular">{{ chatsRemaining }}</span>
                  </div>
                  <div v-if="isFreePlan && lessonsRemaining !== null" class="feature">
                    <span class="feature-label">{{ $t('account.billing.lessonsRemaining') }}</span>
                    <span class="feature-value u-tabular">{{ lessonsRemaining }}</span>
                  </div>
                </div>
                <div v-if="isFreePlan && freebiesBonus" class="bonus-callout">
                  <font-awesome-icon icon="gift" class="bonus-icon" />
                  <span>
                    {{ $t('account.billing.bonusCallout', {
                      chats: freebiesBonus.chats,
                      lessons: freebiesBonus.lessons,
                    }) }}
                  </span>
                </div>
              </div>
              <div v-else class="loading-spinner">
                <font-awesome-icon icon="spinner" spin />
              </div>
            </div>

            <UsageMeter />

            <div class="billing-actions" v-if="!isFreePlan">
              <!-- Stripe billing management -->
              <button v-if="billingProvider === 'stripe' || !billingProvider"
                @click="manageBilling" class="manage-billing-btn" :disabled="isBillingLoading">
                <span v-if="!isBillingLoading">{{ $t('account.billing.manageBilling') }}</span>
                <span v-else class="loading-text">
                  <font-awesome-icon icon="spinner" spin />
                  {{ $t('account.billing.redirecting') }}
                </span>
              </button>
              <!-- IAP billing management -->
              <a v-else-if="billingProvider === 'apple'"
                :href="storeManagementUrl" class="manage-billing-btn" target="_blank">
                {{ $t('account.billing.manageInAppStore', 'Manage in App Store') }}
              </a>
              <a v-else-if="billingProvider === 'google'"
                :href="storeManagementUrl" class="manage-billing-btn" target="_blank">
                {{ $t('account.billing.manageInGooglePlay', 'Manage in Google Play') }}
              </a>
            </div>
            <!-- Restore purchases (native only) -->
            <div class="billing-actions" v-if="canRestorePurchases">
              <button @click="restorePurchases" class="restore-btn" :disabled="isRestoringPurchases">
                <span v-if="!isRestoringPurchases">{{ $t('account.billing.restorePurchases', 'Restore Purchases') }}</span>
                <span v-else class="loading-text">
                  <font-awesome-icon icon="spinner" spin />
                  {{ $t('account.billing.restoring', 'Restoring...') }}
                </span>
              </button>
            </div>
          </div>
        </section>
      </div>

      <!-- Upgrade Options Section -->
      <section
        v-if="isFreePlan || isFoundationPlan"
        id="upgrade-options"
        class="upgrade-options-section"
      >
        <h2 class="section-title">{{ $t('account.upgrade.title') }}</h2>
        <div class="upgrade-description">
          <p v-if="isFreePlan">
            {{ $t('account.upgrade.descriptionFree') }}
          </p>
          <p v-else-if="isFoundationPlan">
            {{ $t('account.upgrade.descriptionFoundation') }}
          </p>
        </div>

        <UpgradeOptions :current-plan-id="currentPlan?.id" :user-country="userCountry" :current-provider="currentPlan?.id === 'free' ? null : (billingProvider || 'stripe')" :on-upgrade="startUpgrade" @iap-purchase-complete="handleIapPurchaseComplete" />
      </section>

      <!-- Contact Us Section -->
      <section class="account-section contact-section">
        <h2 class="section-title">{{ $t('account.contact.title') }}</h2>
        <p class="contact-help">{{ $t('account.contact.description') }}</p>
        <FeedbackForm />
      </section>

      <section class="account-section danger-zone-section">
        <h2 class="section-title">{{ $t('account.dangerZone.title') }}</h2>
        <p class="danger-help">{{ $t('account.dangerZone.deleteDescription') }}</p>
        <button
          type="button"
          class="delete-account-button"
          @click="showDeleteSheet = true"
        >
          {{ $t('account.dangerZone.deleteAccount') }}
        </button>
      </section>
    </div>

    <BottomSheet
      :model-value="showDeleteSheet"
      :title="$t('account.dangerZone.confirmTitle')"
      @update:model-value="onSheetToggle"
    >
      <div class="delete-confirm">
        <p class="delete-confirm-body">{{ $t('account.dangerZone.confirmBody') }}</p>

        <div v-if="showStoreSubscriptionWarning" class="delete-store-warning">
          <p>{{ $t('account.dangerZone.storeSubscriptionWarning', { store: storeName }) }}</p>
          <a
            v-if="storeManagementUrl"
            :href="storeManagementUrl"
            target="_blank"
            rel="noopener"
            class="manage-subscription-link"
          >{{ $t('account.dangerZone.manageSubscription') }}</a>
        </div>
        <p v-else-if="showStripeCancelNote" class="delete-stripe-note">
          {{ $t('account.dangerZone.stripeCancelNote') }}
        </p>
        <p v-else-if="showConservativeWarning" class="delete-store-warning">
          {{ $t('account.dangerZone.subscriptionUnknownWarning') }}
        </p>

        <p v-if="isAppleAccount" class="delete-apple-note">
          {{ $t('account.dangerZone.appleReauthNote') }}
        </p>

        <p class="delete-takes-time">{{ $t('account.dangerZone.confirmTakesTime') }}</p>

        <div class="delete-actions">
          <button
            type="button"
            class="delete-cancel-button"
            :disabled="isDeleting"
            @click="showDeleteSheet = false"
          >{{ $t('account.dangerZone.cancel') }}</button>
          <button
            type="button"
            class="delete-confirm-button"
            :disabled="isDeleting"
            @click="onConfirmDelete"
          >{{ isDeleting ? $t('account.dangerZone.deleting') : $t('account.dangerZone.confirmDelete') }}</button>
        </div>
      </div>
    </BottomSheet>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { getDatabase, ref as dbRef, update, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { authStore } from '@/store/auth';
import { featureFlags } from '@/store/featureFlags.js';
import { notificationCategories } from '@/data/notificationCategories.js';
import { usePushNotifications } from '@/composables/usePushNotifications.js';
import { pricingStore } from '@/store/pricing.js';
import { stripeUtils } from '@/scripts/stripeUtils.js';
import {
  billingConfirmation,
  startBillingWatch,
  hasArmedCheckout,
  resetBillingConfirmation,
} from '@/composables/useBillingConfirmation.js';
import { platformBilling } from '@/scripts/platformBilling.js';
import { iapUtils } from '@/scripts/iapUtils.js';
import { useRoute, useRouter } from 'vue-router';
import { ACCOUNT_UPGRADE_HASH } from '@/constants/routes.js';
import { prefersReducedMotion } from '@/composables/useScrollToSection.js';
import UpgradeOptions from '@/components/UpgradeOptions.vue';
import Modal from '@/components/Modal.vue';
import LanguagePickerGrid from '@/components/LanguagePickerGrid.vue';
import SkeletonLoader from '@/components/SkeletonLoader.vue';
import UsageMeter from '@/components/UsageMeter.vue';
import FeedbackForm from '@/components/FeedbackForm.vue';
import BottomSheet from '@/components/BottomSheet.vue';
import { useAccountDeletion } from '@/composables/useAccountDeletion.js';
import TutorVoicePicker from '@/components/TutorVoicePicker.vue';
import LanguageMixPicker from '@/components/LanguageMixPicker.vue';
import { Languages } from '@/data/Language.js';
import { flagsStore } from '@/store/countryFlags.js';
import { useI18n } from 'vue-i18n';
import { mapLanguageNameToLocale, isSameBaseLanguage } from '@/locales/index.js';
import { useToast } from '@/composables/useToast.js';
import { useTheme } from '@/composables/useTheme.js';
import { useLiveUserInfo } from '@/composables/useLiveUserInfo.js';
import {
  fetchLifetimeUsage,
  getEffectiveFreeLimits,
  getFreebiesBonus,
  hasFreebiesGrant,
} from '@/composables/useUsageCounts.js';

export default {
  name: 'AccountView',
  components: {
    UpgradeOptions,
    Modal,
    LanguagePickerGrid,
    SkeletonLoader,
    UsageMeter,
    FeedbackForm,
    BottomSheet,
    TutorVoicePicker,
    LanguageMixPicker,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const { showToast } = useToast();
    // Live subscription rather than a one-shot read, so writes made elsewhere
    // (notably the tab-bar language switcher writing selectedLanguage) move
    // this page in place instead of leaving it stale until a re-navigation.
    const { userInfo, isLoading } = useLiveUserInfo();
    const currentPlan = ref(null);

    // Any surface that links straight to the plans (the free-tier usage pill)
    // navigates here with ACCOUNT_UPGRADE_HASH. The router's own scrollBehavior
    // cannot honour it: this page resolves its plan asynchronously, so the
    // upgrade section is not in the DOM yet when the router looks for the
    // element. Scroll once, when the plan lands and the section actually
    // exists. A reader who scrolled away in the meantime is left alone.
    let upgradeHashHandled = false;
    watch(currentPlan, async (plan) => {
      if (upgradeHashHandled || !plan) return;
      if (route.hash !== ACCOUNT_UPGRADE_HASH) return;
      upgradeHashHandled = true;
      await nextTick();
      document.getElementById(ACCOUNT_UPGRADE_HASH.slice(1))?.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    });
    const isBillingLoading = ref(false);
    const showSuccessModal = ref(false);
    const confirmationDetails = ref(null);
    const showConfirmationModal = ref(false);
    const isProcessingUpgrade = ref(false);

    // IAP / provider state
    const billingProvider = ref(null);
    const userCountry = ref("US");
    const isNativeApp = platformBilling.isNativeApp();
    const storeManagementUrl = computed(() =>
      platformBilling.getStoreManagementUrl(billingProvider.value)
    );
    const isRestoringPurchases = ref(false);

    // --- Account deletion (Danger zone) ---
    const showDeleteSheet = ref(false);
    const { isDeleting, deleteAccount } = useAccountDeletion();
    // An Apple-linked account (even if the current session came in via Google)
    // needs the Apple re-auth prompt so we can revoke Sign in with Apple.
    const isAppleAccount = computed(() =>
      (getAuth().currentUser?.providerData || []).some(
        (p) => p.providerId === 'apple.com'
      )
    );
    const showStoreSubscriptionWarning = computed(
      () =>
        !isFreePlan.value &&
        (billingProvider.value === 'apple' || billingProvider.value === 'google')
    );
    const showStripeCancelNote = computed(
      () => !isFreePlan.value && billingProvider.value === 'stripe'
    );
    // Restoring re-reads this device's store receipts. Free users can always
    // try (the reinstall case, and the case where a purchase we could not
    // confirm actually did go through). A subscriber may only restore from the
    // store their subscription actually lives on: a Stripe subscriber has no
    // store receipt at all and validateIapReceipt's dual-subscription guard
    // rejects them, and restoring the OTHER store's receipt would swap their
    // billing provider out from under them.
    const canRestorePurchases = computed(() => {
      if (!isNativeApp) return false;
      if (isFreePlan.value) return true;
      return (
        billingProvider.value != null &&
        billingProvider.value === platformBilling.getDeviceStoreProvider()
      );
    });
    // Show a conservative "if you have a subscription, deleting doesn't cancel
    // it" warning whenever we can't confidently render a tailored one: billing
    // not yet loaded (currentPlan null), OR a paid account whose provider is
    // unknown (legacy billing row with no billing.provider — neither the
    // store nor the Stripe notice would fire). A confirmed free plan needs no
    // warning.
    const billingKnown = computed(() => currentPlan.value != null);
    const showConservativeWarning = computed(() => {
      if (!billingKnown.value) return true;
      if (isFreePlan.value) return false;
      return !showStoreSubscriptionWarning.value && !showStripeCancelNote.value;
    });
    const storeName = computed(() =>
      platformBilling.getStoreNameForProvider(billingProvider.value)
    );
    // Block dismissal (scrim tap, Escape, swipe) while the irreversible deletion
    // is in flight, so the user can't mistake a dismissal for a cancellation.
    const onSheetToggle = (open) => {
      if (!open && isDeleting.value) return;
      showDeleteSheet.value = open;
    };
    const onConfirmDelete = async () => {
      const result = await deleteAccount();
      // Re-auth cancelled: close the sheet (already toasted). Server/network
      // failure: keep it open so the user can retry. Success: signOut redirects.
      if (!result.ok && result.reason === 'reauth') {
        showDeleteSheet.value = false;
      }
    };

    // Notification preferences (feature-gated, native only)
    const { registerDevice } = usePushNotifications();
    const notificationPrefs = ref({});
    const isSavingNotificationPrefs = ref(false);
    const notificationPermissionDenied = ref(false);

    // The coerced freebies grant, or null when this account has none worth
    // naming. Drives the launch-bonus callout under the free-plan totals.
    const freebiesBonus = ref(null);

    // Lifetime dialog counts behind the free-tier "remaining" rows. One-shot
    // per user rather than a live meter: the account page is a summary, and the
    // read fans out over the whole dialogs/{uid} subtree. Free tier only, for
    // the same reason UsageMeter is paid-only — a paid account would pay for a
    // read whose rows never render.
    const lifetimeUsage = ref(null);
    // Which uid the current value belongs to, so re-entrant snapshots don't
    // re-read, and a token so a fetch that resolves after the user changed (or
    // after the plan went paid) cannot write stale counts.
    let lifetimeUsageUserId = null;
    let lifetimeUsageToken = 0;
    // Failures release lifetimeUsageUserId so a transient error can recover,
    // but every later users/{uid} write would then re-attempt the full
    // dialogs/{uid} fan-read. A permanent failure (permission denied on a
    // half-deleted account) would retry forever against the exact subtree this
    // is documented to read once, so FAILURES are budgeted per user.
    //
    // Failures, not attempts: a plan flipping free -> paid -> free legitimately
    // invalidates the cached counts and has to re-read, so budgeting attempts
    // would starve a subscription-cycling account of a refresh it needs. The
    // budget is also deliberately NOT cleared by resetLifetimeUsage, or that
    // same cycle would hand a permanently failing account a fresh budget on
    // every interlude and defeat the cap.
    const MAX_USAGE_FAILURES = 2;
    let lifetimeUsageFailures = 0;
    let lifetimeUsageFailureUserId = null;

    const resetLifetimeUsage = () => {
      lifetimeUsageToken++;
      lifetimeUsageUserId = null;
      lifetimeUsage.value = null;
    };

    const loadLifetimeUsage = () => {
      const userId = authStore.getUserId();
      if (!userId || lifetimeUsageUserId === userId) return;
      // A different uid is a fresh budget; the same uid retrying is not.
      if (userId !== lifetimeUsageFailureUserId) {
        lifetimeUsageFailureUserId = userId;
        lifetimeUsageFailures = 0;
      }
      if (lifetimeUsageFailures >= MAX_USAGE_FAILURES) return;
      // A cached RTDB path fires onValue synchronously, so userInfo can move
      // from one account's data straight to another's within a single tick and
      // this watcher never observes the intermediate null. Drop the previous
      // account's counts here too, or its remaining rows render under the new
      // identity until the new read resolves (and forever if that read fails).
      lifetimeUsage.value = null;
      lifetimeUsageUserId = userId;
      const token = ++lifetimeUsageToken;
      fetchLifetimeUsage(userId)
        .then((usage) => {
          if (token === lifetimeUsageToken) lifetimeUsage.value = usage;
        })
        .catch((error) => {
          // Never block the account page over a usage read; the rows just stay
          // hidden, and one later snapshot is allowed to retry (see the failure
          // budget above). A superseded read's failure is not this user's
          // problem, so it does not spend the budget either.
          console.error('Lifetime usage read failed:', error);
          if (token !== lifetimeUsageToken) return;
          lifetimeUsageFailures++;
          lifetimeUsageUserId = null;
        });
    };

    // Null until the counts land (or when they failed), so the template can
    // hide the row rather than render a wrong number. Clamped at zero: a user
    // who was over the old ceiling before a limit change must read 0, not -1.
    const chatsRemaining = computed(() =>
      lifetimeUsage.value && currentPlan.value
        ? Math.max(0, currentPlan.value.lifetimeChats - lifetimeUsage.value.chats)
        : null
    );
    const lessonsRemaining = computed(() =>
      lifetimeUsage.value && currentPlan.value
        ? Math.max(0, currentPlan.value.lifetimeLessons - lifetimeUsage.value.lessons)
        : null
    );

    // Derived billing/notification state, recomputed on every user snapshot
    // (this is the body the old one-shot fetchUserData ran once).
    // immediate: REQUIRED. useLiveUserInfo subscribes during setup above, and
    // RTDB fires onValue synchronously when the path is already cached — which
    // it normally is, because DashboardView, the parent route, keeps its own
    // onValue listener on users/{uid} for the whole dashboard session.
    // Without immediate, that first snapshot lands before this watcher exists,
    // so currentPlan stays null while isLoading is already false.
    watch(
      userInfo,
      (data) => {
        if (!data) {
          // userInfo goes back to null when the signed-in user changes or signs
          // out. Since the billing block is gated on currentPlan, holding the
          // previous user's values here would show their plan under the new
          // identity until (or unless) the next snapshot arrives.
          currentPlan.value = null;
          billingProvider.value = null;
          notificationPrefs.value = {};
          userCountry.value = 'US';
          freebiesBonus.value = null;
          resetLifetimeUsage();
          return;
        }
        notificationPrefs.value = data.notificationPreferences || {};
        billingProvider.value = data.billing?.provider || null;
        userCountry.value = platformBilling.getUserCountry(data);
        // A users/{uid}/freebies grant RAISES the free lifetime ceiling, so the
        // free-tier totals shown here are the effective ones (base + bonus),
        // not the plan constants. Read through the shared helper so this page
        // and the two gate views can never disagree about the same node.
        const freeLimits = getEffectiveFreeLimits(data);
        const plan = pricingStore.getCurrentPlan(data.billing);
        currentPlan.value = {
          ...plan,
          lifetimeChats: freeLimits.chats,
          lifetimeLessons: freeLimits.lessons,
          chatsPerMonth: data.features?.chatsPerMonth ?? pricingStore.plans.free.lifetimeChats,
          lessonsPerMonth: data.features?.lessonsPerMonth ?? pricingStore.plans.free.lifetimeLessons
        };
        // Null rather than a zeroed object so the template's v-if reads as
        // "this account has a grant worth naming". hasFreebiesGrant is the
        // shared definition of that (a bonus that survives coercion, not merely
        // a node that exists), so the callout here and U4's modal trigger
        // cannot drift apart.
        freebiesBonus.value = hasFreebiesGrant(data) ? getFreebiesBonus(data) : null;
        // `plan.id`, not isFreePlan: that computed is declared further down this
        // setup and this watcher runs immediately, before its binding exists.
        if (plan.id === 'free') loadLifetimeUsage();
        else resetLifetimeUsage();
      },
      { immediate: true }
    );

    // UI shows explicit opt-in only: a user is "on" once they have flipped the
    // toggle (which is also what registers the device token). The backend's
    // defaultUserOptIn covers token-holders with no stored pref.
    const isCategoryEnabled = (category) => notificationPrefs.value[category.id] === true;

    const toggleCategory = async (category) => {
      const turningOn = !isCategoryEnabled(category);
      isSavingNotificationPrefs.value = true;
      notificationPermissionDenied.value = false;
      try {
        if (turningOn) {
          const { granted } = await registerDevice();
          if (!granted) {
            notificationPermissionDenied.value = true;
            return;
          }
        }
        const userId = authStore.getUserId();
        if (!userId) return;
        const db = getDatabase();
        await set(
          dbRef(db, `users/${userId}/notificationPreferences/${category.id}`),
          turningOn
        );
        notificationPrefs.value = { ...notificationPrefs.value, [category.id]: turningOn };
      } catch (error) {
        console.error('Error updating notification preference:', error);
        showToast(t('account.notifications.saveError'), 'error');
      } finally {
        isSavingNotificationPrefs.value = false;
      }
    };

    const restorePurchases = async () => {
      isRestoringPurchases.value = true;
      try {
        // restorePurchases() resolves false when the store is not initialized
        // or the restore itself fails; it does not throw. Reporting success
        // regardless would tell a user whose entitlement is still unconfirmed
        // that the recovery we just sent them to worked.
        //
        // Known gap: true only means the store call completed, not that
        // anything was actually restored (no receipts, or a receipt the
        // backend rejects, both resolve true). Distinguishing those needs the
        // billing node to be watched for an actual change; not done here.
        const restored = await iapUtils.restorePurchases();
        if (!restored) {
          showToast(t('account.billing.restoreFailed', 'Failed to restore purchases'), 'error');
          return;
        }
        showToast(t('account.billing.restoreSuccess', 'Purchases restored successfully'), 'success');
      } catch (error) {
        console.error('Error restoring purchases:', error);
        showToast(t('account.billing.restoreFailed', 'Failed to restore purchases'), 'error');
      } finally {
        isRestoringPurchases.value = false;
      }
    };

    // Appearance (theme) control
    const { mode: themeMode, setMode: setThemeMode } = useTheme();
    const themeOptions = [
      { value: 'light', labelKey: 'account.appearance.light', icon: 'sun' },
      { value: 'dark', labelKey: 'account.appearance.dark', icon: 'moon' },
      { value: 'system', labelKey: 'account.appearance.system', icon: 'circle-half-stroke' },
    ];

    // Native language editing
    const isEditingNativeLanguage = ref(false);
    const editNativeLanguage = ref('');
    const isSavingNativeLanguage = ref(false);

    const languageOptions = computed(() => {
      const sorted = {};
      for (const [code, lang] of Object.entries(Languages)) {
        sorted[code] = lang;
      }
      return sorted;
    });

    const editNativeLanguageCode = computed(() => {
      if (!editNativeLanguage.value) return null;
      return mapLanguageNameToLocale(editNativeLanguage.value);
    });

    const currentNativeLanguageCode = computed(() => {
      if (!userInfo.value?.nativeLanguage) return null;
      return mapLanguageNameToLocale(userInfo.value.nativeLanguage);
    });

    const isNativeLanguageDirty = computed(() => {
      return editNativeLanguage.value !== (userInfo.value?.nativeLanguage || '');
    });

    const nativeLanguageConflict = computed(() => {
      if (!editNativeLanguage.value || !userInfo.value?.selectedLanguage) return false;
      return isSameBaseLanguage(
        editNativeLanguageCode.value,
        userInfo.value.selectedLanguage,
      );
    });

    const startEditNativeLanguage = () => {
      editNativeLanguage.value = userInfo.value?.nativeLanguage || '';
      isEditingNativeLanguage.value = true;
    };

    const cancelEditNativeLanguage = () => {
      isEditingNativeLanguage.value = false;
    };

    const onPickNativeLanguage = (code) => {
      const lang = Languages[code];
      if (lang) {
        editNativeLanguage.value = lang.name;
      }
    };

    const saveNativeLanguage = async () => {
      if (nativeLanguageConflict.value) return;
      isSavingNativeLanguage.value = true;
      try {
        // Captured before the await, and handed to setNativeLanguage: a sign-out
        // that lands between the write and its resolution must not apply this
        // account's language to whoever is signed in by then.
        const userId = authStore.getUserId();
        const db = getDatabase();
        await update(dbRef(db, `users/${userId}`), {
          nativeLanguage: editNativeLanguage.value,
        });
        userInfo.value.nativeLanguage = editNativeLanguage.value;
        authStore.setNativeLanguage(editNativeLanguage.value, { forUserId: userId });
        isEditingNativeLanguage.value = false;
      } catch (error) {
        console.error('Error saving native language:', error);
      } finally {
        isSavingNativeLanguage.value = false;
      }
    };

    const isFreePlan = computed(() => {
      return currentPlan.value?.id === 'free';
    });

    const selectedBillingCycle = computed(() => {
      return confirmationDetails.value?.selectedBillingCycle || 'monthly';
    });

    const isFoundationPlan = computed(() => {
      return currentPlan.value?.id === 'foundation';
    });

    const handleIapPurchaseComplete = () => {
      // The live user subscription picks up the entitlement write; nothing to refetch.
    };

    const startUpgrade = async (planType) => {
      const planName = planType.split('-')[0];
      const billingCycle = planType.split('-')[1];
      const plan = pricingStore.plans[planName];
      const lookupKey = billingCycle === 'month' ? plan.monthlyLookupKey : plan.yearlyLookupKey;

      if (isFreePlan.value) {
        // Free tier users do not need an upgrade confirmation modal; they can go straight to checkout.
        isProcessingUpgrade.value = true;
        try {
          await stripeUtils.startCheckout(lookupKey);
        } catch (error) {
          console.error('Error starting checkout:', error);
          showToast(t('account.upgrade.upgradeError'), 'error');
        } finally {
          isProcessingUpgrade.value = false;
        }
        return;
      }

      confirmationDetails.value = {
        nameKey: plan.nameKey,
        price: billingCycle === 'month' ? plan.monthlyPrice : plan.yearlyPrice,
        yearlyTotal: plan.yearlyTotal,
        chatsPerMonth: plan.chatsPerMonth,
        lessonsPerMonth: plan.lessonsPerMonth,
        lookupKey: lookupKey,
        selectedBillingCycle: billingCycle === 'month' ? 'monthly' : 'yearly'
      };
      isProcessingUpgrade.value = false;
      showConfirmationModal.value = true;
    };

    const cancelUpgrade = () => {
      confirmationDetails.value = null;
      showConfirmationModal.value = false;
      isProcessingUpgrade.value = false;
    };

    const confirmUpgrade = async () => {
      if (!confirmationDetails.value) return;

      isProcessingUpgrade.value = true;
      try {
        await stripeUtils.startCheckout(confirmationDetails.value.lookupKey);
        showConfirmationModal.value = false;
      } catch (error) {
        console.error('Error creating upgrade checkout session:', error);
        showToast(t('account.upgrade.upgradeError'), 'error');
      } finally {
        isProcessingUpgrade.value = false;
      }
    };

    const translateLanguageName = (languageName) => {
      if (!languageName) return null;

      // Find the language code by matching the English name
      const languageCode = Object.entries(Languages).find(
        ([code, lang]) => lang.name === languageName
      )?.[0];

      if (languageCode) {
        return t(`languages.${languageCode}`);
      }

      // Fallback to original name if no match found
      return languageName;
    };

    const proficiencyKeys = ['beginner', 'elementary', 'intermediate', 'upperIntermediate', 'advanced'];
    const reasonKeys = ['career', 'travel', 'fun', 'relationships', 'culture', 'school', 'other'];

    const translateStoredValue = (value, keys, i18nPrefix) => {
      if (!value) return null;
      if (keys.includes(value)) {
        return t(`${i18nPrefix}.${value}`);
      }
      return value;
    };

    const translateProficiency = (value) => {
      return translateStoredValue(value, proficiencyKeys, 'onboarding.newLanguage.proficiency');
    };

    const translateReason = (value) => {
      return translateStoredValue(value, reasonKeys, 'onboarding.newLanguage.reasons');
    };

    // Per-language account list (flag on). Mirrors the server's entitlement
    // (helpers/users.isLanguageEntitled): Fluency shows every held language;
    // Free/Foundation — including downgraded ex-Fluency, whose dormant
    // languages stay stored — show only the active one. Active first, then
    // alphabetical, matching the switcher (useLearningLanguages).
    // An account holding selectedLanguage but no languagePreferences produces an
    // empty list here and the row reads "Not set". That is not new: the flag-off
    // rows that used to name selectedLanguage directly were unreachable, because
    // multiLanguageAccess/all had been true for every user long before the flag
    // was deleted. Giving those accounts a selectedLanguage fallback would be a
    // real display change and belongs in its own change, not in a flag removal.
    const heldLanguageCodes = computed(() => {
      const held = Object.keys(userInfo.value?.languagePreferences || {});
      const entitled =
        userInfo.value?.features?.multiLingual === true
          ? held
          : held.filter((code) => code === userInfo.value?.selectedLanguage);
      const active = userInfo.value?.selectedLanguage;
      return entitled.sort((a, b) =>
        a === active ? -1 : b === active ? 1 : languageName(a).localeCompare(languageName(b))
      );
    });
    const languageName = (code) => Languages[code]?.nativeName || code;
    // Proficiency/reason joined with a separator, skipping whichever is unset
    // so no stray dot renders for partial data.
    const languageMeta = (code) => {
      const prefs = userInfo.value?.languagePreferences?.[code] || {};
      return [
        translateProficiency(prefs.proficiency),
        translateReason(prefs.reasonForLearning),
      ]
        .filter(Boolean)
        .join(' · ');
    };

    const manageBilling = async () => {
      isBillingLoading.value = true;
      try {
        const billingUrl = await stripeUtils.createBillingSession();
        if (billingUrl) {
          window.location.href = billingUrl;
        }
      } catch (error) {
        console.error('Error creating billing portal session:', error);
        showToast(t('errors.billing.portalFailed'), 'error');
        isBillingLoading.value = false;
      }
    };

    const closeSuccessModal = () => {
      // The showSuccessModal watcher below clears the ?checkoutSuccess query, so
      // dismissing via the button, the backdrop, Escape, or drag all converge.
      showSuccessModal.value = false;
    };

    // Drop only the checkoutSuccess flag, preserving any unrelated query params.
    const clearCheckoutQuery = () => {
      if (route.query.checkoutSuccess === undefined) return;
      const query = { ...route.query };
      delete query.checkoutSuccess;
      router.replace({ query });
    };

    // Show the success modal only once the purchased plan has actually landed;
    // the live subscription reflects the new tier on its own.
    const onCheckoutConfirmed = async () => {
      showSuccessModal.value = true;
      resetBillingConfirmation();
    };

    // Decide how to treat a return from checkout (native app-link/instant
    // upgrade, or a full-page web redirect from Stripe).
    const finalizeCheckoutReturn = () => {
      const outcome = route.query.checkoutSuccess;
      if (outcome === 'false') {
        // Canceled checkout (web cancel_url) — clear any armed plan so it can't
        // leak into a later watch, and drop the stale query so a later refresh
        // (e.g. mid instant-upgrade watch) doesn't re-abort on this branch.
        resetBillingConfirmation();
        clearCheckoutQuery();
        return;
      }
      if (outcome !== 'true') return;

      // The entitlement watch already confirmed the plan before routing here
      // (native return, or web instant upgrade on this same page).
      if (billingConfirmation.phase === 'confirmed') {
        onCheckoutConfirmed();
        return;
      }

      // Web full-page return from Stripe: the in-memory watch was wiped by the
      // reload. Re-arm from the persisted plan and wait for the webhook before
      // declaring success. Legacy/cleared state falls back to the previous
      // immediate success modal so nothing hangs.
      if (hasArmedCheckout()) {
        startBillingWatch();
      } else {
        showSuccessModal.value = true;
      }
    };

    // A watch started on this page (web instant upgrade or web Stripe return)
    // resolves here rather than through the overlay's route push.
    watch(
      () => billingConfirmation.phase,
      (phase) => {
        if (phase === 'confirmed') onCheckoutConfirmed();
      }
    );

    // Clear the ?checkoutSuccess query whenever the success modal closes — the
    // Modal can be dismissed via backdrop click, Escape, or drag, none of which
    // route through closeSuccessModal, so leaving the query would re-open the
    // modal on the next refresh.
    watch(showSuccessModal, (isOpen) => {
      if (!isOpen) clearCheckoutQuery();
    });

    onMounted(() => {
      finalizeCheckoutReturn();

      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/pricing-table.js';
      script.async = true;
      document.head.appendChild(script);
    });

    return {
      userInfo,
      currentPlan,
      freebiesBonus,
      chatsRemaining,
      lessonsRemaining,
      isLoading,
      isBillingLoading,
      isFreePlan,
      isFoundationPlan,
      heldLanguageCodes,
      languageName,
      languageMeta,
      translateLanguageName,
      translateProficiency,
      translateReason,
      manageBilling,
      pricingStore,
      showSuccessModal,
      showConfirmationModal,
      selectedBillingCycle,
      closeSuccessModal,
      confirmationDetails,
      isProcessingUpgrade,
      startUpgrade,
      cancelUpgrade,
      confirmUpgrade,
      isEditingNativeLanguage,
      editNativeLanguage,
      editNativeLanguageCode,
      currentNativeLanguageCode,
      isNativeLanguageDirty,
      isSavingNativeLanguage,
      languageOptions,
      nativeLanguageConflict,
      startEditNativeLanguage,
      cancelEditNativeLanguage,
      saveNativeLanguage,
      onPickNativeLanguage,
      flagsStore,
      billingProvider,
      showDeleteSheet,
      isDeleting,
      isAppleAccount,
      showStoreSubscriptionWarning,
      showStripeCancelNote,
      billingKnown,
      showConservativeWarning,
      storeName,
      onSheetToggle,
      onConfirmDelete,
      isNativeApp,
      storeManagementUrl,
      canRestorePurchases,
      isRestoringPurchases,
      restorePurchases,
      userCountry,
      handleIapPurchaseComplete,
      themeMode,
      themeOptions,
      setThemeMode,
      featureFlags,
      notificationCategories,
      notificationPrefs,
      isCategoryEnabled,
      toggleCategory,
      isSavingNotificationPrefs,
      notificationPermissionDenied,
    };
  }
};
</script>

<style scoped>
.account-view {
  padding: 1rem;
  min-height: 100%;
  background-color: var(--al-surface-page);
  overflow-y: auto;
}

@media (max-width: 767px) {
  .account-view {
    padding-top: 1rem;
  }

  .page-title {
    margin-top: 2.5rem;
    margin-bottom: 2rem;
    font-weight: 600;
    position: relative;
    z-index: 10;
  }
}

.container {
  max-width: 100%;
  margin: 0 auto;
  padding: 0 1rem;
}

.page-title {
  font-size: 1.75rem;
  color: var(--al-text);
  margin-bottom: 1.5rem;
  font-weight: 700;
  text-align: center;
  padding: 0 1rem;
}

.account-sections {
  display: grid;
  /* minmax(0, 1fr), not bare 1fr: 1fr implies minmax(auto, 1fr), so one card
     whose content's min-width exceeds the track (e.g. the appearance
     segmented control on narrow phones) inflates the shared track and pushes
     EVERY card in the grid past the screen's right edge. Capping the track's
     min at 0 keeps the cards at container width; overflow stays the child's
     own problem to solve. */
  grid-template-columns: minmax(0, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.account-section {
  background-color: var(--al-surface-raised);
  border-radius: var(--al-radius-md);
  box-shadow: var(--al-shadow-sm);
  padding: 1.5rem;
  height: 100%;
}

/* The contact and danger-zone cards live outside the .account-sections grid, so
   the grid's 1.5rem gap never separates them and .account-section has no margin
   of its own — leaving the danger zone flush against the card above it. Restore
   the same rhythm for these standalone cards. */
.contact-section,
.danger-zone-section {
  margin-top: 1.5rem;
}

.section-title {
  font-size: 1.25rem;
  color: var(--al-text);
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid var(--al-border);
  padding-bottom: 0.75rem;
}

.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--al-text-muted);
  font-size: 1rem;
  padding: 2rem 0;
}

.info-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--al-border);
  min-height: 44px;
}

.info-label {
  font-weight: 600;
  color: var(--al-text-muted);
  margin-bottom: 0.5rem;
}

.info-value {
  color: var(--al-text);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.native-lang-display {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

/* flex-basis 100% so the hint takes its own line under the value and the Edit
   link, in both the stacked mobile layout and the two-column desktop one. */
.info-hint {
  flex: 1 0 100%;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--al-text-muted);
}

/* Per-language list (multi-language flag on) */
.languages-list .info-value {
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
}

.language-entry {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.language-entry .language-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
}

.language-entry .active-badge {
  background-color: var(--al-primary-soft);
  color: var(--al-text);
  border-radius: var(--al-radius-pill);
  padding: 0.1rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.language-entry .lang-meta {
  color: var(--al-text-muted);
  font-size: 0.85rem;
  /* Phones: the meta doesn't fit beside the name + Active badge, and the
     flex-wrap + margin-left:auto combo dropped it onto its own line hugging
     the right edge. Give it a full-width line aligned under the name (indented
     past the 24px flag) instead. Desktop restores the inline right-aligned
     placement below. */
  flex-basis: 100%;
  margin-left: calc(24px + 0.5rem);
}

.native-lang-flag {
  width: 1.5rem;
  height: auto;
  border-radius: 0.25rem;
  border: 1px solid var(--al-border);
  display: block;
  flex-shrink: 0;
}

.edit-link {
  background: none;
  border: none;
  color: var(--al-primary-hover);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  /* 44px touch floor without shifting the inline layout */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  min-width: 44px;
  padding: 0 0.25rem;
  margin-left: 0.25rem;
}

.edit-link:hover {
  text-decoration: underline;
}

.native-lang-modal-help {
  color: var(--al-text);
  font-size: 0.9375rem;
  margin: 0 0 1rem;
  line-height: 1.5;
}

.native-lang-modal-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  width: 100%;
}

.conflict-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  /* --al-danger (#dc3545) on --al-danger-bg is only ~3.4:1, failing AA for this
     small text. Uses the theme-aware danger-text token (darker red in light,
     light rose in dark) matching utilities.css's .alert-danger convention. */
  color: var(--al-danger-text);
  background-color: var(--al-danger-bg);
  border: 1px solid var(--al-danger-border);
  border-radius: var(--al-radius-sm);
  padding: 0.625rem 0.875rem;
  margin-top: 1rem;
  font-size: 0.875rem;
}

.conflict-icon {
  flex-shrink: 0;
}

.billing-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.current-plan h3 {
  font-size: 1.125rem;
  color: var(--al-text);
  margin-bottom: 0.75rem;
  font-weight: 500;
}

.plan-name {
  font-size: 1rem;
  font-weight: 600;
  /* al-primary on white is ~3.3:1, failing AA for this normal-size text;
     al-primary-hover clears 4.5:1. */
  color: var(--al-primary-hover);
  margin-bottom: 0.5rem;
}

/* Launch-bonus callout. DESIGN.md reserves the warm accent (#EB882D, the
   --al-orange token) for achievement / premium emphasis and explicitly not for
   primary actions, which is what this is. The accent carries the icon and the
   rule; the copy stays on --al-text because orange on either surface misses AA
   at body size. --al-warm-soft resolves to a solid warm tint in light mode and
   a low-alpha wash in dark, so the block reads the same in both themes. */
.bonus-callout {
  display: flex;
  align-items: center;
  gap: var(--al-space-sm);
  margin-top: var(--al-space-md);
  padding: var(--al-space-sm) var(--al-space-md);
  border-left: 3px solid var(--al-orange);
  border-radius: var(--al-radius-sm);
  background-color: var(--al-warm-soft);
  color: var(--al-text);
  font-size: 0.875rem;
  line-height: 1.4;
}

.bonus-icon {
  color: var(--al-orange);
  font-size: 1rem;
  flex-shrink: 0;
}

.billing-date {
  color: var(--al-text-muted);
  font-size: 0.875rem;
}

.billing-actions {
  margin-top: 1rem;
}

.manage-billing-btn {
  background-color: var(--al-primary);
  color: var(--al-white);
  border: none;
  border-radius: var(--al-radius-sm);
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.manage-billing-btn:disabled {
  background-color: var(--al-gray);
  cursor: not-allowed;
  transform: none;
}

.manage-billing-btn:not(:disabled):hover {
  background-color: var(--al-primary-hover);
  transform: translateY(-2px);
}

.loading-text {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.restore-btn {
  background-color: transparent;
  color: var(--al-text-muted);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-sm);
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.restore-btn:hover {
  background-color: var(--al-surface-sunken);
  color: var(--al-text);
}

.restore-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

a.manage-billing-btn {
  text-decoration: none;
  text-align: center;
}

.upgrade-description {
  text-align: center;
  color: var(--al-text);
  font-size: 1.1rem;
  margin-bottom: 2rem;
}

.contact-help {
  color: var(--al-text-muted);
  font-size: 0.9375rem;
  margin-bottom: 1rem;
}

/* FeedbackForm ships its own light-mode card chrome for the landing page;
   strip it here so it sits flush inside the account-section card and
   follows the page's dark mode instead of forcing a white box. */
.contact-section :deep(.feedback-form) {
  max-width: 100%;
  background-color: transparent;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
  margin-bottom: 0;
}

.contact-section :deep(.field label) {
  color: var(--al-text);
}

.contact-section :deep(.field input),
.contact-section :deep(.field textarea) {
  background-color: var(--al-surface-sunken);
  color: var(--al-text);
  border-color: var(--al-border);
}

.contact-section :deep(.field input:focus),
.contact-section :deep(.field textarea:focus) {
  border-color: var(--al-primary);
}

.contact-section :deep(.field input[aria-invalid="true"]),
.contact-section :deep(.field textarea[aria-invalid="true"]) {
  border-color: var(--al-danger);
}

.contact-section :deep(.field input:disabled),
.contact-section :deep(.field textarea:disabled) {
  background-color: var(--al-surface-muted);
}

.contact-section :deep(.status-error) {
  color: var(--al-danger-text);
}

.contact-section :deep(.status-success) {
  background-color: color-mix(in srgb, var(--al-success) 12%, var(--al-surface-sunken));
  color: var(--al-text);
  border-color: color-mix(in srgb, var(--al-success) 35%, var(--al-border));
}

.contact-section :deep(.submit-button) {
  background-color: var(--al-primary-hover);
}

.contact-section :deep(.submit-button:disabled) {
  background-color: var(--al-gray-300);
}

.success-content {
  text-align: center;
}

.success-icon {
  width: 60px;
  height: 60px;
  background: color-mix(in srgb, var(--al-success) 10%, var(--al-surface-raised));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.check-icon {
  font-size: 40px;
  color: var(--al-success-dark);
}

.success-content p {
  color: var(--al-text);
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 2rem;
}

.close-btn {
  background-color: var(--al-success-dark);
  color: var(--al-white);
  border: none;
  padding: 0.75rem 2rem;
  border-radius: var(--al-radius-sm);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 48px;
}

.close-btn:hover {
  background-color: var(--al-success-dark);
  box-shadow: var(--al-shadow-md);
  transform: translateY(-2px);
}

@keyframes modalPop {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: translateY(20px);
}


.plan-summary {
  text-align: center;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: var(--al-surface-sunken);
  border-radius: var(--al-radius-md);
}

.plan-summary .plan-name {
  /* al-primary on white is ~3.3:1, failing AA for this normal-size text;
     al-primary-hover clears 4.5:1. */
  color: var(--al-primary-hover);
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.plan-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--al-text);
}

.period {
  font-size: 1rem;
  color: var(--al-text-muted);
  font-weight: normal;
}

.yearly-note {
  color: var(--al-text-muted);
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.features-summary {
  margin-bottom: 1.5rem;
  text-align: center;
}

.feature-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  color: var(--al-text);
  font-size: 1rem;
  max-width: 300px;
  margin: 0 auto;
}

.feature-icon {
  color: var(--al-primary);
  font-size: 1.1rem;
  width: 20px;
}

.feature-label {
  margin-right: 0.25rem;
}

.billing-note {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background-color: var(--al-primary-soft);
  border-radius: var(--al-radius-sm);
  color: var(--al-text);
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
}

.info-icon {
  color: var(--al-primary);
  font-size: 1.1rem;
}

.confirmation-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-sm);
  background-color: var(--al-surface-raised);
  color: var(--al-text-muted);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px;
}

.cancel-btn:hover {
  background-color: var(--al-surface-sunken);
  color: var(--al-text);
}

.confirm-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: var(--al-radius-sm);
  /* AA: white on --al-primary is ~3.3:1; primary-hover passes at ~4.67:1. */
  background-color: var(--al-primary-hover);
  color: var(--al-white);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.confirm-btn:not(:disabled):hover {
  background-color: var(--al-primary-hover);
  transform: translateY(-1px);
}

.confirm-btn:disabled {
  background-color: var(--al-gray);
  cursor: not-allowed;
  transform: none;
}

/* Tablet and larger screens */
@media (min-width: 768px) {
  .account-view {
    padding: 2rem;
  }

  .container {
    max-width: 1000px;
  }

  .page-title {
    font-size: 2.5rem;
    padding-top: 1rem;
  }

  .account-sections {
    /* Same minmax(0, ...) guard as the mobile rule above. */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
  }

  .info-row {
    flex-direction: row;
  }

  .info-label {
    flex: 0 0 40%;
    margin-bottom: 0;
  }

  /* The multi-language list is too wide to sit in the right 60% next to the
     label; keep it stacked so the languages get their own full-width line. */
  .info-row.languages-list {
    flex-direction: column;
  }

  .info-row.languages-list .info-label {
    flex: none;
    margin-bottom: 0.75rem;
  }

  /* Wide screens fit name + badge + meta on one row; three classes so this
     wins over the base .language-entry .lang-meta rule by specificity (the
     prod CSS minifier can reorder equal-specificity duplicates). */
  .info-row.languages-list .lang-meta {
    flex-basis: auto;
    margin-left: auto;
  }


  .success-icon {
    width: 80px;
    height: 80px;
  }

  .success-content p {
    font-size: 1.1rem;
  }

  .plan-price {
    font-size: 2rem;
  }
}

/* Large screens */
@media (min-width: 1024px) {
  .account-view {
    padding: 3rem;
  }
}

/* Appearance (theme) segmented control */
.appearance-control {
  display: flex;
  gap: 4px;
  padding: 4px;
  background: var(--al-surface-sunken);
  border: 1px solid var(--al-border);
  border-radius: var(--al-radius-md);
  width: fit-content;
  /* On narrow phones the three labeled buttons outgrow the card; cap at the
     card's content width and let the options compress (see min-width: 0 +
     ellipsis below) rather than spill past the card's right padding. */
  max-width: 100%;
}
.appearance-option {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  min-width: 0;
  flex: 1 1 auto;
  padding: 0 12px;
  border: 0;
  border-radius: var(--al-radius-sm);
  background: transparent;
  color: var(--al-text-muted);
  font: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move), color var(--al-dur-short) var(--al-ease-move);
}
.appearance-option span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* Tighter option chrome on phones so the three labels fit un-truncated;
   the ellipsis above stays as the net for extra-long translations. */
@media (max-width: 767px) {
  .appearance-option {
    gap: 5px;
    padding: 0 8px;
    font-size: 0.9375rem;
  }
}
.appearance-option.active {
  background: var(--al-surface-raised);
  color: var(--al-text);
  box-shadow: var(--al-shadow-sm);
}
.appearance-option:focus-visible {
  outline: 0;
  /* Solid fallback first: the focus indicator must survive WebViews without color-mix(). */
  box-shadow: 0 0 0 3px rgba(45, 144, 235, 0.3);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--al-primary) 30%, transparent);
}

/* Notifications section */
.notifications-help {
  color: var(--al-text-muted);
  font-size: 0.9375rem;
  margin-bottom: 1rem;
}

.notification-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--al-border);
}

.toggle-label {
  font-weight: 600;
  color: var(--al-text);
}

.toggle-description {
  color: var(--al-text-muted);
  font-size: 0.875rem;
}

.toggle-switch {
  flex-shrink: 0;
  width: 52px;
  height: 32px;
  border: 1px solid var(--al-border);
  border-radius: 16px;
  background: var(--al-surface-sunken);
  position: relative;
  cursor: pointer;
  padding: 0;
  transition: background-color var(--al-dur-short) var(--al-ease-move);
}

.toggle-switch .toggle-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--al-surface-raised);
  box-shadow: var(--al-shadow-sm);
  transition: transform var(--al-dur-short) var(--al-ease-move);
}

.toggle-switch.on {
  background: var(--al-primary);
  border-color: var(--al-primary);
}

.toggle-switch.on .toggle-knob {
  transform: translateX(20px);
  background: var(--al-white);
}

.toggle-switch:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle-switch:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px rgba(45, 144, 235, 0.3);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--al-primary) 30%, transparent);
}
</style>

<!-- Unscoped: Modal is teleported to body, so scoped selectors don't reach it. -->
<style>
@media (min-width: 768px) {
  .modal-dialog.modal-dialog-native-lang {
    max-width: 720px;
  }
}

/* --- Danger zone / account deletion --- */
.danger-help {
  color: var(--al-text-muted);
  margin-bottom: 12px;
}

.delete-account-button {
  padding: 10px 18px;
  border-radius: var(--al-radius-md);
  border: 1px solid var(--al-danger-border);
  background-color: var(--al-danger-bg);
  color: var(--al-danger-text);
  font-weight: 600;
  cursor: pointer;
  transition: background-color var(--al-dur-short) var(--al-ease-move),
    color var(--al-dur-short) var(--al-ease-move);
}

.delete-account-button:hover {
  background-color: var(--al-danger);
  color: var(--al-white);
}

.delete-confirm {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 8px;
}

.delete-confirm-body {
  color: var(--al-text);
  line-height: 1.5;
}

.delete-store-warning {
  padding: 12px 14px;
  border-radius: var(--al-radius-md);
  background-color: var(--al-danger-bg);
  color: var(--al-danger-text);
  border: 1px solid var(--al-danger-border);
}

.manage-subscription-link {
  display: inline-block;
  margin-top: 8px;
  font-weight: 600;
  color: var(--al-primary);
}

.delete-stripe-note,
.delete-apple-note,
.delete-takes-time {
  color: var(--al-text-muted);
  font-size: 14px;
  line-height: 1.5;
}

.delete-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}

.delete-cancel-button,
.delete-confirm-button {
  flex: 1;
  padding: 12px;
  border-radius: var(--al-radius-md);
  font-weight: 600;
  cursor: pointer;
}

.delete-cancel-button {
  border: 1px solid var(--al-border);
  background-color: var(--al-surface-raised);
  color: var(--al-text);
}

.delete-confirm-button {
  border: 1px solid var(--al-danger);
  background-color: var(--al-danger);
  color: var(--al-white);
}

.delete-cancel-button:disabled,
.delete-confirm-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>